/**
 * Community Sharing Service
 *
 * Handles community folder and design operations.
 * Public browsing works without auth, sharing requires authentication.
 */

import { supabase, isSupabaseConfigured } from '@/config/supabase';
import { getSession } from './auth';
import type {
  CommunityFolder,
  CommunityDesign,
  CreateFolderData,
  ShareDesignData,
  FoldersResponse,
  FolderDetailResponse,
} from '@/types';
import type { StoredDesign } from './storage';

/**
 * Generate a URL-friendly slug from a folder name
 */
function generateSlug(name: string): string {
  const baseSlug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);

  // Add random suffix for uniqueness
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  return `${baseSlug}-${randomSuffix}`;
}

/**
 * Convert database row to CommunityFolder
 */
function toFolder(row: Record<string, unknown>): CommunityFolder {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    slug: row.slug as string,
    visibility: row.visibility as 'public' | 'link_only',
    coverImageUrl: row.cover_image_url as string | undefined,
    designCount: row.design_count as number,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
    creatorName: row.creator_name as string | undefined,
  };
}

/**
 * Convert database row to CommunityDesign
 */
function toDesign(row: Record<string, unknown>): CommunityDesign {
  return {
    id: row.id as string,
    folderId: row.folder_id as string,
    userId: row.user_id as string,
    sourceDesignId: row.source_design_id as string | undefined,
    productId: row.product_id as string,
    name: row.name as string | undefined,
    frontBlobUrl: row.front_blob_url as string,
    backBlobUrl: row.back_blob_url as string | undefined,
    thumbnailUrl: row.thumbnail_url as string | undefined,
    metadata: (row.metadata as Record<string, unknown>) || {},
    createdAt: new Date(row.created_at as string),
  };
}

/**
 * Fetch public community folders (paginated)
 * No authentication required
 */
export async function fetchPublicFolders(page = 1, limit = 20): Promise<FoldersResponse> {
  if (!isSupabaseConfigured()) {
    return { folders: [], total: 0, page, limit, hasMore: false };
  }

  const offset = (page - 1) * limit;

  // Get total count
  const { count, error: countError } = await supabase
    .from('community_folders')
    .select('*', { count: 'exact', head: true })
    .eq('visibility', 'public');

  if (countError) {
    console.error('Failed to count community folders:', countError);
    throw new Error('Failed to load community folders');
  }

  // Get paginated folders
  const { data, error } = await supabase
    .from('community_folders')
    .select('*')
    .eq('visibility', 'public')
    .order('updated_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Failed to fetch community folders:', error);
    throw new Error('Failed to load community folders');
  }

  const total = count || 0;
  const folders = (data || []).map(toFolder);

  return {
    folders,
    total,
    page,
    limit,
    hasMore: offset + folders.length < total,
  };
}

/**
 * Fetch a folder by its slug (public or link-only)
 * No authentication required
 */
export async function fetchFolderBySlug(slug: string): Promise<CommunityFolder | null> {
  if (!isSupabaseConfigured()) {
    return null;
  }

  const { data, error } = await supabase
    .from('community_folders')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('Failed to fetch folder by slug:', error);
    throw new Error('Failed to load folder');
  }

  return data ? toFolder(data) : null;
}

/**
 * Fetch designs in a folder
 * No authentication required for public/link-only folders
 */
export async function fetchFolderDesigns(folderId: string): Promise<CommunityDesign[]> {
  if (!isSupabaseConfigured()) {
    return [];
  }

  const { data, error } = await supabase
    .from('community_designs')
    .select('*')
    .eq('folder_id', folderId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch folder designs:', error);
    throw new Error('Failed to load designs');
  }

  return (data || []).map(toDesign);
}

/**
 * Fetch folder with designs (combined query)
 */
export async function fetchFolderDetail(slug: string): Promise<FolderDetailResponse | null> {
  const folder = await fetchFolderBySlug(slug);
  if (!folder) {
    return null;
  }

  const designs = await fetchFolderDesigns(folder.id);

  return { folder, designs };
}

/**
 * Create a new community folder
 * Authentication required
 */
export async function createFolder(data: CreateFolderData): Promise<CommunityFolder> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  const slug = generateSlug(data.name);

  const { data: folder, error } = await supabase
    .from('community_folders')
    .insert({
      user_id: session.user.id,
      name: data.name.trim(),
      description: data.description?.trim() || null,
      slug,
      visibility: data.visibility,
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      // Unique constraint violation on slug
      throw new Error('This folder name already exists. Please choose a different name.');
    }
    console.error('Failed to create folder:', error);
    throw new Error('Failed to create folder');
  }

  return toFolder(folder);
}

/**
 * Update a community folder
 * Authentication required, must be owner
 */
export async function updateFolder(
  folderId: string,
  data: Partial<Pick<CreateFolderData, 'name' | 'description' | 'visibility'>>
): Promise<CommunityFolder> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  const updateData: Record<string, unknown> = {};
  if (data.name !== undefined) updateData.name = data.name.trim();
  if (data.description !== undefined) updateData.description = data.description?.trim() || null;
  if (data.visibility !== undefined) updateData.visibility = data.visibility;

  const { data: folder, error } = await supabase
    .from('community_folders')
    .update(updateData)
    .eq('id', folderId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    console.error('Failed to update folder:', error);
    throw new Error('Failed to update folder');
  }

  return toFolder(folder);
}

/**
 * Soft delete a community folder
 * Authentication required, must be owner
 * Items can be restored within 30 days
 */
export async function deleteFolder(folderId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  // Verify ownership before soft delete
  const { data: folder, error: fetchError } = await supabase
    .from('community_folders')
    .select('id')
    .eq('id', folderId)
    .eq('user_id', session.user.id)
    .single();

  if (fetchError || !folder) {
    throw new Error('Folder not found or access denied');
  }

  // Use soft delete function
  const { error } = await supabase.rpc('soft_delete_folder', {
    folder_uuid: folderId,
  });

  if (error) {
    console.error('Failed to delete folder:', error);
    throw new Error('Failed to delete folder');
  }
}

/**
 * Restore a soft-deleted folder
 * Authentication required, must be owner
 */
export async function restoreFolder(folderId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  // Verify ownership
  const { data: folder, error: fetchError } = await supabase
    .from('community_folders')
    .select('id')
    .eq('id', folderId)
    .eq('user_id', session.user.id)
    .single();

  if (fetchError || !folder) {
    throw new Error('Folder not found or access denied');
  }

  const { error } = await supabase.rpc('restore_folder', {
    folder_uuid: folderId,
  });

  if (error) {
    console.error('Failed to restore folder:', error);
    throw new Error('Failed to restore folder');
  }
}

/**
 * Soft delete a community design
 * Authentication required, must be owner
 */
export async function deleteDesign(designId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  // Verify ownership
  const { data: design, error: fetchError } = await supabase
    .from('community_designs')
    .select('id')
    .eq('id', designId)
    .eq('user_id', session.user.id)
    .single();

  if (fetchError || !design) {
    throw new Error('Design not found or access denied');
  }

  const { error } = await supabase.rpc('soft_delete_design', {
    design_uuid: designId,
  });

  if (error) {
    console.error('Failed to delete design:', error);
    throw new Error('Failed to delete design');
  }
}

/**
 * Restore a soft-deleted design
 * Authentication required, must be owner
 */
export async function restoreDesign(designId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  // Verify ownership
  const { data: design, error: fetchError } = await supabase
    .from('community_designs')
    .select('id')
    .eq('id', designId)
    .eq('user_id', session.user.id)
    .single();

  if (fetchError || !design) {
    throw new Error('Design not found or access denied');
  }

  const { error } = await supabase.rpc('restore_design', {
    design_uuid: designId,
  });

  if (error) {
    console.error('Failed to restore design:', error);
    throw new Error('Failed to restore design');
  }
}

/**
 * Get user's soft-deleted items (for recovery UI)
 */
export interface DeletedItem {
  itemType: 'folder' | 'design';
  id: string;
  name: string;
  folderId: string | null;
  deletedAt: Date;
}

export async function getDeletedItems(): Promise<DeletedItem[]> {
  if (!isSupabaseConfigured()) {
    return [];
  }

  const { session } = await getSession();
  if (!session?.user) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_deleted_items')
    .select('*')
    .eq('user_id', session.user.id)
    .order('deleted_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch deleted items:', error);
    return [];
  }

  return (data || []).map((item) => ({
    itemType: item.item_type as 'folder' | 'design',
    id: item.id,
    name: item.name,
    folderId: item.folder_id,
    deletedAt: new Date(item.deleted_at),
  }));
}

/**
 * Upload design images to community storage
 * Returns public URLs for the uploaded images
 */
async function uploadDesignToStorage(
  folderId: string,
  designId: string,
  frontBlobUrl: string,
  backBlobUrl?: string
): Promise<{ frontUrl: string; backUrl?: string }> {
  // Fetch the front blob
  const frontResponse = await fetch(frontBlobUrl);
  const frontBlob = await frontResponse.blob();

  // Upload front image
  const frontPath = `community/${folderId}/${designId}/front.png`;
  const { error: frontError } = await supabase.storage
    .from('designs')
    .upload(frontPath, frontBlob, {
      contentType: 'image/png',
      upsert: true,
    });

  if (frontError) {
    console.error('Failed to upload front image:', frontError);
    throw new Error('Failed to upload design image');
  }

  const { data: frontData } = supabase.storage.from('designs').getPublicUrl(frontPath);

  const result: { frontUrl: string; backUrl?: string } = {
    frontUrl: frontData.publicUrl,
  };

  // Upload back image if provided
  if (backBlobUrl) {
    const backResponse = await fetch(backBlobUrl);
    const backBlob = await backResponse.blob();

    const backPath = `community/${folderId}/${designId}/back.png`;
    const { error: backError } = await supabase.storage.from('designs').upload(backPath, backBlob, {
      contentType: 'image/png',
      upsert: true,
    });

    if (backError) {
      console.error('Failed to upload back image:', backError);
      // Continue without back image
    } else {
      const { data: backData } = supabase.storage.from('designs').getPublicUrl(backPath);
      result.backUrl = backData.publicUrl;
    }
  }

  return result;
}

/**
 * Share a design to a community folder
 * Authentication required, must own the folder
 */
export async function shareDesignToFolder(data: ShareDesignData): Promise<CommunityDesign> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  // Verify folder ownership
  const { data: folder, error: folderError } = await supabase
    .from('community_folders')
    .select('id')
    .eq('id', data.folderId)
    .eq('user_id', session.user.id)
    .single();

  if (folderError || !folder) {
    throw new Error('You can only share to your own folders');
  }

  // Generate a new design ID
  const designId = crypto.randomUUID();

  // Upload images to storage
  const { frontUrl, backUrl } = await uploadDesignToStorage(
    data.folderId,
    designId,
    data.frontBlobUrl,
    data.backBlobUrl
  );

  // Create the design record
  const { data: design, error } = await supabase
    .from('community_designs')
    .insert({
      id: designId,
      folder_id: data.folderId,
      user_id: session.user.id,
      source_design_id: data.sourceDesignId || null,
      product_id: data.productId,
      name: data.name?.trim() || null,
      front_blob_url: frontUrl,
      back_blob_url: backUrl || null,
      thumbnail_url: data.thumbnailUrl || frontUrl,
      metadata: data.metadata || {},
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to share design:', error);
    throw new Error('Failed to share design');
  }

  return toDesign(design);
}

/**
 * Remove a design from a community folder
 * Authentication required, must own the design
 */
export async function removeDesignFromFolder(designId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { session } = await getSession();
  if (!session?.user) {
    throw new Error('Authentication required');
  }

  const { error } = await supabase
    .from('community_designs')
    .delete()
    .eq('id', designId)
    .eq('user_id', session.user.id);

  if (error) {
    console.error('Failed to remove design:', error);
    throw new Error('Failed to remove design');
  }
}

/**
 * Fetch user's own folders
 * Authentication required
 */
export async function fetchMyFolders(): Promise<CommunityFolder[]> {
  if (!isSupabaseConfigured()) {
    return [];
  }

  const { session } = await getSession();
  if (!session?.user) {
    return [];
  }

  const { data, error } = await supabase
    .from('community_folders')
    .select('*')
    .eq('user_id', session.user.id)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch my folders:', error);
    throw new Error('Failed to load your folders');
  }

  return (data || []).map(toFolder);
}

/**
 * Generate a shareable link for a folder
 */
export function generateShareLink(slug: string): string {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  return `${baseUrl}/community/${slug}`;
}

/**
 * Convert a community design to a StoredDesign for loading into the editor
 */
export async function loadCommunityDesignToEditor(design: CommunityDesign): Promise<StoredDesign> {
  // Fetch the blobs from URLs
  const frontResponse = await fetch(design.frontBlobUrl);
  const frontBlob = await frontResponse.blob();

  let backBlob: Blob | null = null;
  if (design.backBlobUrl) {
    const backResponse = await fetch(design.backBlobUrl);
    backBlob = await backResponse.blob();
  }

  // Convert to StoredDesign format
  return {
    id: design.id,
    frontBlob,
    backBlob,
    createdAt: design.createdAt,
    metadata: {
      productId: design.productId,
      frontStyle: design.metadata.style || 'community',
      frontTemplate: design.metadata.layout || design.metadata.theme || 'shared',
      designName: design.name || 'Community Design',
      // Keep frontTheme for backwards compatibility
      frontTheme: design.metadata.theme,
    },
  };
}
